import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { App, Button, Input, Popconfirm, Select, Statistic, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { exportTickets, listCheckins, manualCheckin, undoCheckin } from '../api';
import { CheckinRow, CheckinStats, errorText, formatVn } from '../contract';
import { seatLabel } from '../lib/seat-text';
import styles from '../styles.module.scss';

interface Props {
  eventId: number;
}

const REFRESH_MS = 10_000;
const EMPTY_STATS: CheckinStats = { issued: 0, checkedIn: 0, pending: 0 };

/** Lưu blob thành file tải về. */
const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

/** Nút "Huỷ check-in": hỏi lý do (bắt buộc, không để trống) rồi mới gọi. */
const UndoButton = ({ onConfirm }: { onConfirm: (reason: string) => Promise<void> }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = reason.trim().length > 0;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popconfirm
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setReason('');
      }}
      title="Huỷ check-in này?"
      description={
        <Input
          autoFocus
          value={reason}
          maxLength={300}
          placeholder="Lý do huỷ (bắt buộc)"
          aria-label="Lý do huỷ check-in"
          onChange={(e) => setReason(e.target.value)}
          onPressEnter={submit}
          style={{ width: 240, marginTop: 8 }}
        />
      }
      okText="Huỷ check-in"
      cancelText="Đóng"
      okButtonProps={{ danger: true, disabled: !valid, loading: busy }}
      onConfirm={submit}
    >
      <Button size="small" danger type="link">
        Huỷ check-in
      </Button>
    </Popconfirm>
  );
};

/** Tab Check-in: số đã vào / tổng vé, bảng giờ vào, nhập mã tay, xuất Excel (SPEC mục 5). */
const CheckinTab = ({ eventId }: Props) => {
  const { message } = App.useApp();
  const [rows, setRows] = useState<CheckinRow[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<CheckinStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [group, setGroup] = useState<string | undefined>();
  const [knownGroups, setKnownGroups] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [manualBusy, setManualBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const requestRef = useRef(0);

  const reload = useCallback(
    async (showSpinner = true) => {
      const ticket = ++requestRef.current;
      if (showSpinner) setLoading(true);
      try {
        const res = await listCheckins(eventId, { group, page, limit });
        if (ticket !== requestRef.current) return;
        setRows(res.data);
        setTotal(res.total);
        setStats(res.stats ?? EMPTY_STATS);
        const seen = res.data.map((r) => r.guestGroup).filter((g): g is string => !!g);
        if (seen.length) setKnownGroups((cur) => Array.from(new Set([...cur, ...seen])).sort());
      } catch (e) {
        if (ticket === requestRef.current) message.error(errorText(e, 'Không tải được danh sách check-in.'));
      } finally {
        if (ticket === requestRef.current) setLoading(false);
      }
    },
    [eventId, group, page, limit, message]
  );

  useEffect(() => {
    reload();
    return () => {
      requestRef.current += 1; // bỏ kết quả đang bay khi đổi bộ lọc / rời tab
    };
  }, [reload]);

  // Làm mới im lặng mỗi 10 giây; dừng khi rời tab (component bị huỷ) hoặc đổi bộ lọc.
  useEffect(() => {
    const timer = window.setInterval(() => reload(false), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [reload]);

  const groupOptions = useMemo(() => {
    const set = new Set(knownGroups);
    if (group) set.add(group);
    return Array.from(set)
      .sort()
      .map((g) => ({ value: g, label: g }));
  }, [knownGroups, group]);

  const onManual = async () => {
    const shortCode = code.trim().toUpperCase();
    if (shortCode.length !== 6) return;
    setManualBusy(true);
    try {
      const res = await manualCheckin(eventId, shortCode);
      message.success(`Đã check-in: ${res?.ticket?.displayName || shortCode}`);
      setCode('');
      setPage(1);
      await reload(false);
    } catch (e) {
      message.error(errorText(e, 'Không check-in được mã này.'));
    } finally {
      setManualBusy(false);
    }
  };

  const onUndo = async (row: CheckinRow, reason: string) => {
    try {
      await undoCheckin(eventId, row.id, reason);
      message.success(`Đã huỷ check-in của ${row.displayName || 'khách'}.`);
      await reload(false);
    } catch (e) {
      message.error(errorText(e, 'Không huỷ được check-in.'));
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await exportTickets(eventId);
      saveBlob(blob, filename);
    } catch (e) {
      message.error(errorText(e, 'Không xuất được file Excel.'));
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnsType<CheckinRow> = [
    {
      title: 'Giờ vào',
      dataIndex: 'at',
      width: 150,
      render: (v: string) => <span className={styles.nowrap}>{formatVn(v)}</span>,
    },
    { title: 'Tên trên vé', dataIndex: 'displayName', render: (v: string) => v || '—' },
    {
      title: '@username',
      dataIndex: 'username',
      render: (v: string | null) => (v ? `@${v}` : <span className={styles.unlinked}>— chưa gắn —</span>),
    },
    { title: 'Nhóm', dataIndex: 'guestGroup', width: 110, render: (v: string | null) => v || '—' },
    {
      title: 'Ghế',
      dataIndex: 'seat',
      width: 140,
      render: (v: CheckinRow['seat']) =>
        v ? <span className={styles.nowrap}>{seatLabel(v.tableName, v.number)}</span> : '—',
    },
    { title: 'Người quét', dataIndex: 'byName', render: (v: string) => v || '—' },
    {
      title: 'Trạng thái',
      width: 110,
      render: (_: unknown, r) =>
        r.undoneAt ? (
          <Tooltip title={`Huỷ bởi ${r.undoneByName || '—'} lúc ${formatVn(r.undoneAt)}: ${r.undoReason || ''}`}>
            <Tag>Đã huỷ</Tag>
          </Tooltip>
        ) : (
          <Tag color="success">Đã vào</Tag>
        ),
    },
    {
      title: '',
      width: 130,
      render: (_: unknown, r) => (r.undoneAt ? null : <UndoButton onConfirm={(reason) => onUndo(r, reason)} />),
    },
  ];

  return (
    <div>
      <section className={styles.card} aria-label="Số liệu check-in">
        <div className={styles.statusBar}>
          <Statistic title="Đã vào / tổng vé phát" value={stats.checkedIn} suffix={`/ ${stats.issued}`} />
          <Statistic title="Chưa vào" value={Math.max(0, stats.pending)} style={{ marginLeft: 32 }} />
        </div>
        <span className={styles.subtle}>Tự làm mới mỗi 10 giây.</span>
      </section>

      <section className={styles.card} aria-label="Danh sách check-in">
        <h3 className={styles.cardTitle}>Lượt check-in</h3>
        <div className={styles.toolbar}>
          <Select
            allowClear
            placeholder="Nhóm khách"
            style={{ width: 160 }}
            value={group}
            onChange={(v) => {
              setPage(1);
              setGroup(v);
            }}
            options={groupOptions}
          />
          <Button icon={<ReloadOutlined />} onClick={() => reload(false)} aria-label="Tải lại danh sách" />
          <span style={{ flex: 1 }} />
          <Input
            value={code}
            maxLength={6}
            placeholder="Nhập mã tay (6 ký tự)"
            aria-label="Mã tay"
            style={{ width: 200 }}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onPressEnter={onManual}
          />
          <Button type="primary" onClick={onManual} loading={manualBusy} disabled={code.trim().length !== 6}>
            Check-in
          </Button>
          <Button icon={<DownloadOutlined />} onClick={onExport} loading={exporting}>
            Xuất Excel
          </Button>
        </div>
        <Table<CheckinRow>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Chưa có lượt check-in nào.' }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100],
            showTotal: (t) => `${t} lượt`,
            onChange: (p, s) => {
              setPage(s !== limit ? 1 : p);
              setLimit(s);
            },
          }}
        />
      </section>
    </div>
  );
};

export default CheckinTab;
